import { useState } from 'react';
import { Camera, Image as ImageIcon } from 'lucide-react';
import {
  EmptyState,
  ErrorState,
  Skeleton,
  StatusBadge,
  SubBar,
} from '@/design/components';
import { useMe } from '@/auth/useMe';
import { useImageUrl } from '@/features/products/api';
import { formatDate } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { uploadOpsPhoto, useOpsPhoto } from '../photos';
import {
  displayStatusLabel,
  displayStatusTone,
  useDisplayMutations,
  useMyCampaigns,
  type DisplayCampaign,
} from './api';

/**
 * ויטרינה, בצד הסניף (PRD 5.9.3).
 *
 * תמונת היעד למעלה, הצילום של הסניף מתחתיה. ככה רואים את שתיהן יחד
 * ואפשר להשוות, וזו כל הנקודה.
 */
export function DisplayPage() {
  const campaigns = useMyCampaigns();

  if (campaigns.isPending) return <Skeleton className="m-4 h-40" />;
  if (campaigns.isError) return <ErrorState onRetry={() => void campaigns.refetch()} />;

  const all = campaigns.data ?? [];

  return (
    <div className="grid content-start gap-4">
      <SubBar title="ויטרינה" subtitle="איך הסניף צריך להיראות" />

      <div className="grid gap-4 px-4 pb-8">
        {all.length === 0 && (
          <EmptyState
            title="אין משימות ויטרינה"
            description="כשההנהלה תשלח סידור חדש הוא יופיע כאן."
          />
        )}

        {all.map((campaign) => (
          <Campaign key={campaign.campaign_id} campaign={campaign} />
        ))}
      </div>
    </div>
  );
}

function Campaign({ campaign }: { campaign: DisplayCampaign }) {
  const { me } = useMe();
  const reference = useImageUrl(campaign.reference_image_path);
  const mine = useOpsPhoto(campaign.submission?.photo_path);
  const { submit } = useDisplayMutations();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = (file: File) => {
    if (!me?.primaryBranchId || !me.employeeId) {
      setError('אין לך סניף, ולכן אי אפשר להגיש.');
      return;
    }
    setBusy(true);
    setError(null);

    uploadOpsPhoto(me.primaryBranchId, 'display', file)
      .then((path) =>
        submit.mutateAsync({
          campaign_id: campaign.campaign_id,
          branch_id: me.primaryBranchId as string,
          submitted_by: me.employeeId,
          photo_path: path,
        }),
      )
      .catch((caught) => setError(errorMessage(caught)))
      .finally(() => setBusy(false));
  };

  const status = campaign.submission?.status;

  return (
    <section className="grid gap-3 rounded-md border border-line bg-raised p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <span className="text-title font-medium text-ink">{campaign.title}</span>
        {status && (
          <StatusBadge tone={displayStatusTone[status]}>
            {displayStatusLabel[status]}
          </StatusBadge>
        )}
      </div>

      {campaign.due_date && (
        <p className="m-0 text-label text-muted">עד {formatDate(campaign.due_date)}</p>
      )}

      {campaign.instructions && (
        <p className="m-0 whitespace-pre-line text-body">{campaign.instructions}</p>
      )}

      <div className="grid gap-1">
        <span className="text-body-sm text-muted">כך זה צריך להיראות</span>
        {reference.data ? (
          <img src={reference.data} alt="תמונת היעד" className="w-full rounded-sm" />
        ) : (
          <span className="grid h-32 place-items-center rounded-sm bg-olive-soft">
            <ImageIcon size={28} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
          </span>
        )}
      </div>

      {mine.data && (
        <div className="grid gap-1">
          <span className="text-body-sm text-muted">מה שהסניף העלה</span>
          <img src={mine.data} alt="הצילום של הסניף" className="w-full rounded-sm" />
        </div>
      )}

      {campaign.submission?.feedback && (
        <p className="m-0 rounded-sm border border-line p-2 text-body-sm">
          הערת ההנהלה: {campaign.submission.feedback}
        </p>
      )}

      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {status !== 'approved' && (
        <label className="inline-flex min-h-11 w-fit cursor-pointer items-center gap-2 rounded-none border border-charcoal bg-charcoal px-6 font-display text-[20px] font-semibold text-on-charcoal">
          <Camera size={20} strokeWidth={1.75} aria-hidden="true" />
          {busy ? 'מעלה…' : status ? 'להעלות צילום מתוקן' : 'לצלם את הוויטרינה'}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) upload(file);
            }}
            className="sr-only"
          />
        </label>
      )}
    </section>
  );
}
